import { useEffect, useState, useRef } from 'react'
import videojs from 'video.js'
import { gtmTrack } from '../../utils/track'
import analytics from '../../public/scripts/comscore'
import style from '../../styles/components/layout/search'
import styles from '../../styles/components/video-player'

const VideoPlayer = ({ video, poster, muted = true, autoplay = true, title = '', label = '' }) => {
  const [showControls, setShowControls] = useState(true)
  const [initialized, setInitialized] = useState(false)
  const [player, setPlayer] = useState(null)
  const timeOut = useRef(null)
  const videoNode = useRef(null)
  const unmuteButton = useRef(null)
  const hasLoggedStartEvent = useRef(null)
  const cleaningUp = useRef(null)
  const startOfVideo = useRef(true)
  let quartiles = []

  const { src, type, isLive, captions } = video

  useEffect(() => {
    if (videoNode && !initialized && !player) {
      setPlayer(setUpPlayer())
    }

    if (player) {
      analytics.PlatformApi.setPlatformAPI(analytics.PlatformApi.PlatformApis.WebBrowser)

      let myPublisherConfig = new analytics.configuration.PublisherConfiguration({ publisherId: 34363161 })

      analytics.configuration.setApplicationName('Cheddar')
      analytics.configuration.addClient(myPublisherConfig)
      analytics.configuration.setUsagePropertiesAutoUpdateMode(
        analytics.configuration.UsagePropertiesAutoUpdateMode.FOREGROUND_AND_BACKGROUND
      )
      analytics.configuration.enableImplementationValidationMode()
      analytics.start()

      let sa = new analytics.StreamingAnalytics()
      let cm = new analytics.StreamingAnalytics.ContentMetadata()

      cm.setUniqueId(src)
      cm.setProgramTitle(isLive ? 'cheddar-live' : label || title)
      cm.setGenreName('News')
      cm.setPublisherName('Cheddar')
      cm.setStationTitle('Cheddar')
      sa.setMetadata(cm)

      player.src({ src, type })
      player.on('volumechange', () => onVolumeChange(player))
      player.on('playing', (e) => onPlaying(e, sa, cm))
      player.on('pause', () => onPause(sa))
      player.on('ended', () => onVideoEnded(sa))
      player.on('timeupdate', () => onTimeUpdate())
    }

    return () => {
      cleaningUp.current = true
      if (isLive && initialized) {
        window.onunload = null
      }
    }
  }, [videoNode, initialized, player, src, type, cleaningUp])

  const updateMetadata = (sa, cm) => {
    cm.setLength(Math.round(player.duration() * 1000) || 0)
    cm.setGenreName('News')
    cm.setPublisherName('Cheddar')
    cm.setDictionaryClassificationC4('Cheddar')
    cm.setMediaType(getMediaType(player))
    sa.setMetadata(cm)
  }

  const getMediaType = (player) => {
    const videoLength = player.duration()
    const tenMinutes = 600

    if (isLive) {
      return analytics.StreamingAnalytics.ContentMetadata.ContentType.LIVE
    }

    if (videoLength >= tenMinutes) {
      return analytics.StreamingAnalytics.ContentMetadata.ContentType.LONG_FORM_ON_DEMAND
    }

    return analytics.StreamingAnalytics.ContentMetadata.ContentType.SHORT_FORM_ON_DEMAND
  }

  const videoAttrs = (extras = {}) => ({ ...extras, category: 'VOD', label: label || title })

  const videoRemains = () => ({
    'Time Elapsed': player?.currentTime(),
    'Time Remaining': player?.duration() - player?.currentTime()
  })

  const onPlaying = (e, comscore, metadata) => {
    if (startOfVideo.current) {
      updateMetadata(comscore, metadata)
      comscore.createPlaybackSession()
      startOfVideo.current = false
    }

    if (!hasLoggedStartEvent?.current) {
      quartiles = [0.25, 0.5, 0.75, 0.9]
      gtmTrack('Start', videoAttrs())
      hasLoggedStartEvent.current = true
    } else if (e.type === 'playing') {
      gtmTrack('Resume', videoAttrs(videoRemains()))
    }

    comscore.notifyPlay()
  }

  const onPause = (comscore) => {
    if (player && videoRemains()['Time Remaining'] > 0) {
      gtmTrack('Pause', videoAttrs({ ...videoRemains(), ...videoAttrs({ value: player.currentTime() }) }))
    }
    comscore.notifyPause()
  }

  const onVideoEnded = (comscore) => {
    gtmTrack('Complete', videoAttrs({ value: player?.duration() }))
    comscore.notifyEnd()
    startOfVideo.current = true
  }

  const onTimeUpdate = () => {
    const pctPlayed = player?.currentTime() / player?.duration()
    if (quartiles.length > 0 && pctPlayed > quartiles[0]) {
      const quartile = quartiles.shift()
      gtmTrack(`Progress_${parseInt(quartile * 100, 10)}`, videoAttrs({ value: player.currentTime() }))
    }
  }

  const setUpPlayer = () => {
    const player = createPlayer()
    setInitialized(true)

    if (muted) {
      setupUnmuteControl(player)
    }

    return player
  }

  const createPlayer = () => {
    const overrideNative = !videojs.browser.IS_SAFARI && !(navigator.userAgent.match(/iPhone/i) || navigator.userAgent.match(/iPad/i))

    window.videojs = videojs

    return window.videojs(
      videoNode.current,
      {
        muted,
        textTrackSettings: false,
        html5: {
          nativeTextTracks: !overrideNative,
          hls: { overrideNative },
          nativeAudioTracks: !overrideNative,
          nativeVideoTracks: !overrideNative
        }
      },
      () => delayControlHide()
    )
  }

  const onVolumeChange = (player) => {
    if (!player.muted() && unmuteButton) {
      unmuteButton.current.style.display = 'none'
      gtmTrack('Unmute', videoAttrs())
    }
  }

  const setupUnmuteControl = (player) => {
    if (player) {
      const button = player.addChild('button')
      button.addClass('unmute')
      unmuteButton.current = button.el()

      const muteHandler = (e) => {
        e.stopPropagation()
        player.muted(false)
        unmuteButton.current.style.display = 'none'
      }

      unmuteButton.current.onclick = (e) => muteHandler(e)
      unmuteButton.current.ontouchend = (e) => muteHandler(e)
    }
  }

  const showControlsFunc = () => {
    if (timeOut.current) {
      clearTimeout(timeOut.current)
    }
    setShowControls(true)
    delayControlHide()
  }

  const delayControlHide = () => {
    timeOut.current = setTimeout(() => {
      if (player && cleaningUp) {
        setShowControls(false)
      }
    }, 2200)
  }

  const getClassName = () => {
    const is_live = isLive ? ' is_live' : ''
    const show_controls = showControls ? ' show_controls' : ''

    return `video_player${is_live}${show_controls}`
  }

  const getCaptions = () => {
    if (isLive || !captions) {
      return null
    }

    return <track kind='captions' src={captions} srcLang='en' label='English' default />
  }

  return (
    <>
      <div data-testid='video_player' className={getClassName()} onMouseMove={showControlsFunc}>
        <div data-vjs-player id='content_video' className='video-js'>
          <video ref={videoNode} muted={muted} className='video-js' crossOrigin='anonymous' poster={poster} playsInline autoPlay={autoplay}>
            {getCaptions()}
          </video>
        </div>
      </div>
      <style global jsx>
        {styles}
      </style>
    </>
  )
}

export default VideoPlayer
